import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useBusinessLocations } from "@/features/business/hooks/businessLocationQueries";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { useSearchParams } from "react-router-dom";
import { AppointmentEdit } from "../components/AppointmentEdit";
import { useCalendarEventOperation } from "@/features/calendar-events/hooks/useCalendarEventOperation";
import { useAppointment, useCreateAppointment } from "../hooks/appointmentQueries";
import { fromAppointmentToRebookingRequest } from "../types/appointmentTypes";

export function AppointmentAddPage() {
  const { goToAppointmentDetails } = useAppNavigation();
  const { businessId, locationId } = useCurrentBusiness();
  const [params] = useSearchParams();
  const copying = params.has("copyFrom");
  const sourceId = params.get("copyFrom") ?? "";
  const source = useAppointment(sourceId);
  const locations = useBusinessLocations(businessId ?? "");
  const mutation = useCreateAppointment();
  const captureOperation = useCalendarEventOperation();
  const original = source.data;
  const locationAvailable = original?.locationId && locations.data?.some(
    (location) => location.id === original.locationId && location.businessId === businessId,
  );
  if (copying && (!sourceId || source.isLoading || source.error || !original || locations.isLoading || locations.error || !locationAvailable)) {
    return <Page><PageHeader title="Book again" /><PageContent>
      {!sourceId ? <EmptyState message="Choose an appointment to book again." />
        : source.isLoading || locations.isLoading ? <RowSkeleton />
        : source.error ? <ErrorState message={source.error.message} onRetry={() => void source.refetch()} />
        : locations.error ? <ErrorState message={locations.error.message} onRetry={() => void locations.refetch()} />
        : !original ? <EmptyState message="Appointment not found." />
        : <EmptyState message="The original location is no longer available. Create an appointment at an available location instead." />}
    </PageContent></Page>;
  }
  return (
    <AppointmentEdit
      key={`${businessId}:${locationId}:${sourceId}`}
      appointment={copying && original ? fromAppointmentToRebookingRequest(original) : { status: "confirmed" }}
      initialClientId={copying ? undefined : params.get("clientId") ?? undefined}
      initialDateKey={copying ? undefined : params.get("date") ?? undefined}
      initialTime={copying ? undefined : params.get("time") ?? undefined}
      initialStartAt={copying ? undefined : params.get("startAt") ?? undefined}
      title={copying ? "Book again" : "Client appointment"}
      submitButtonText="Create appointment"
      isSubmitting={mutation.isPending}
      onSubmit={async (data, services) => {
        const isCurrent = captureOperation();
        if (!data.startAt || !data.endAt) throw new Error("Choose an available time.");
        if (copying) {
          const currentLocations = await locations.refetch();
          if (currentLocations.error) throw currentLocations.error;
          if (!currentLocations.data?.some((location) => location.id === data.locationId && location.businessId === businessId)) {
            throw new Error("The original location is no longer available. Create an appointment at an available location instead.");
          }
        }
        if (!isCurrent()) return;
        const created = await mutation.mutateAsync({
          businessId, locationId: data.locationId, clientId: data.clientId,
          startAt: data.startAt, endAt: data.endAt, status: "confirmed",
          price: data.price, depositAmount: data.depositAmount, notes: data.notes, services,
        });
        if (isCurrent()) goToAppointmentDetails(created.id, true);
      }}
    />
  );
}
